import { t } from '../i18n/idioma';
import { VERSAO_FRONT } from '../suporte/coleta';

/**
 * A interface sempre na versão publicada (techspec-atualizacao-do-cliente, Renato 02/11/2026).
 *
 * O servidor manda a versão do web (`nv`) ao conectar no hub (login e reconexão) e quando um deploy só da interface
 * troca a versão. Nenhuma consulta periódica no cliente. Versão diferente da desta página:
 * - entrando no mundo (o mundo ainda não abriu) → recarrega na hora, antes de entrar;
 * - no mundo, aba escondida → recarrega na hora (caçada e coleta seguem no servidor);
 * - no mundo, aba visível → espera sair de combate e de duelo, mostra "Atualizando o jogo…" por 5 s e recarrega.
 * Uma trava por versão impede laço: se a página nova ainda vier velha (cache na frente), não tenta de novo.
 */

const SHA = /^[0-9a-f]{7,40}$/;
const AVISO_MS = 5000;
const ESPERA_OCUPADO_MS = 2000;

let mundoAberto = false;
let ocupado: () => boolean = () => false;
let pendente: string | null = null;
let relogio = 0;

/** O mundo abriu (ou fechou). Antes de abrir, a versão errada recarrega sem esperar. */
export function definirMundoAberto(aberto: boolean): void {
  mundoAberto = aberto;
}

/** Em combate ou duelo agora: a recarga espera. O WorldScreen registra; fora dele, nunca ocupado. */
export function definirOcupado(fn: (() => boolean) | null): void {
  ocupado = fn ?? (() => false);
}

/** A versão desta página é de build (sha)? Em desenvolvimento ("dev") nunca recarrega. */
function paginaVersionada(): boolean {
  return SHA.test(VERSAO_FRONT);
}

function jaTentou(versao: string): boolean {
  try {
    return sessionStorage.getItem(`uoidle.atualizou.${versao}`) === '1';
  } catch {
    return false;
  }
}

function recarregar(versao: string): void {
  try { sessionStorage.setItem(`uoidle.atualizou.${versao}`, '1'); } catch { /* sem storage: recarrega assim mesmo */ }
  location.reload();
}

/** O `nv` do servidor. */
export function receberVersaoWeb(versao: string): void {
  if (!paginaVersionada() || !SHA.test(versao) || versao === VERSAO_FRONT) return;
  if (jaTentou(versao)) return;
  if (!mundoAberto || document.hidden) {
    recarregar(versao);
    return;
  }
  pendente = versao;
  agendar();
}

function agendar(): void {
  window.clearTimeout(relogio);
  const versao = pendente;
  if (!versao) return;
  if (document.hidden) {
    recarregar(versao);
    return;
  }
  if (ocupado()) {
    relogio = window.setTimeout(agendar, ESPERA_OCUPADO_MS);
    return;
  }
  mostrarAviso();
  relogio = window.setTimeout(() => {
    // Entrou em combate durante o aviso: espera de novo.
    if (ocupado() && !document.hidden) {
      esconderAviso();
      relogio = window.setTimeout(agendar, ESPERA_OCUPADO_MS);
      return;
    }
    recarregar(versao);
  }, AVISO_MS);
}

let aviso: HTMLDivElement | null = null;

function mostrarAviso(): void {
  if (aviso) return;
  aviso = document.createElement('div');
  aviso.className = 'atualizando';
  aviso.setAttribute('role', 'status');
  aviso.textContent = t('sistema.atualizando');
  document.body.appendChild(aviso);
}

function esconderAviso(): void {
  aviso?.remove();
  aviso = null;
}

/**
 * Um pedaço do código sob demanda (a Wiki) que sumiu no deploy: a aba antiga pede um arquivo que não existe mais.
 * Recarrega uma vez por versão desta página.
 */
export function instalarAtualizacao(): void {
  window.addEventListener('vite:preloadError', e => {
    const chave = `preload.${VERSAO_FRONT}`;
    if (jaTentou(chave)) return;
    e.preventDefault();
    recarregar(chave);
  });
  // Voltou para a aba com uma versão esperando: a aba escondida recarrega na hora.
  document.addEventListener('visibilitychange', () => {
    if (pendente && document.hidden) recarregar(pendente);
  });
}
